import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getTokenWithVK } from "@/api/auth";
import { useAuthStore } from "@/store/auth";
import { showNotification } from "@/utils/network";
import { useTranslation } from "react-i18next";
import { sanitizeNextPath } from "@/utils/authRedirect";

const VK_NEXT_PATH_KEY = "vk_next_path";

function getPayloadParams(payload: string | null) {
  if (!payload) return null;

  try {
    return new URLSearchParams(JSON.parse(payload));
  } catch {
    return null;
  }
}

export function useVkCallback() {
  const navigate = useNavigate();
  const { setTokenResponse } = useAuthStore();
  const { t } = useTranslation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payloadParams = getPayloadParams(params.get("payload"));

    const code = params.get("code") ?? payloadParams?.get("code");
    const deviceId = params.get("device_id") ?? payloadParams?.get("device_id");
    const state = params.get("state") ?? payloadParams?.get("state");

    const savedState = sessionStorage.getItem("vk_oauth_state");
    const codeVerifier = sessionStorage.getItem("vk_code_verifier");

    const redirectUri =
      import.meta.env.VITE_VK_REDIRECT_URI || `${window.location.origin}/`;

    if (!code || !deviceId || !state || state !== savedState || !codeVerifier)
      return;

    sessionStorage.removeItem("vk_oauth_state");
    sessionStorage.removeItem("vk_code_verifier");
    const nextPath = sanitizeNextPath(sessionStorage.getItem(VK_NEXT_PATH_KEY));
    sessionStorage.removeItem(VK_NEXT_PATH_KEY);

    window.history.replaceState(null, document.title, window.location.pathname);

    getTokenWithVK({ code, deviceId, codeVerifier, redirectUri })
      .then((res) => {
        setTokenResponse(res);
        navigate(nextPath || "/");
      })
      .catch(() => {
        showNotification({
          text: t("errors.generic"),
          type: "error",
        });
      });
  }, [navigate, setTokenResponse, t]);
}
